import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  makeStyles,
} from "@material-ui/core";
import { ExpertsApplyModelDialogProps} from "../types";
import { AssessmentActivities, ExpertiseType, SubjectAddModel, SubjectModel, TeachingActivity } from "../../domain/SubjectModel";
import { Controller, useForm, useWatch } from "react-hook-form";
import { AssessmentActivityLookupsLocal, AssessmentExpertiseLookupsLocal, QualificationTypeLookupsLocal, TeachingExpertiseLookupsLocal, qualificationLevelsService, subjectsService } from "../../services";
import { useLookup } from "@ofqual/portal-core/core/hooks/useLookup";
import { Select } from "@ofqual/portal-components/components/Select";
import { useDispatch } from "react-redux";
import { addExpertise } from "../../redux/actions";
import { Error } from "../common/Error";


const useStyles = makeStyles(() => ({
  actions: {
    padding: 15,
  },
}));

const defaultValues: Partial<SubjectAddModel> = {
  subjectId: undefined,
  qualificationTypeId: undefined,
  expertiseType: undefined,
  selectedAssessmentActivities: undefined,
  teachingActivity: undefined,
  qualificationLevels: undefined
};

export const AddExpertiseDialog: React.FC<
  ExpertsApplyModelDialogProps
  > = ({ item, onClose }) => {
  
    const { handleSubmit, control, errors } =
      useForm<SubjectAddModel>({
        defaultValues,
      });

    const [
      subjectLookup,
      qualificationTypesLookupsLocal,
      qualificationLevelsLookups,
      assessmentActivityLookup,
      assessmentExpertiseLookup,
      teachingExpertiseLookupsLocal,
    ] = useLookup(
      {
        service: subjectsService,
        id: "id",
        label: (s) => (s.subjectTitle + (s.qualificationType ? (' : ' + s.qualificationType) : ' ') + (s.qualificationLevel ? (' : ' + s.qualificationLevel) : ' '))
      },
      QualificationTypeLookupsLocal,
      {
        service: qualificationLevelsService,
        id: "id",
        label : "levelDescription"
      },
      AssessmentActivityLookupsLocal,
      AssessmentExpertiseLookupsLocal,
      TeachingExpertiseLookupsLocal,

    );    

    const { expertiseType } = useWatch<SubjectAddModel>({
      control,
      name: ["expertiseType"],
    });

    const classes = useStyles();
  
    const dispatch = useDispatch();
    const onSubmit = (expertise: SubjectAddModel) => {
    let subjectGuid = crypto.randomUUID();
    // shape the data into a SubjectModel
      let subject: SubjectModel = {
        subjectId: expertise.subjectId,
        id: subjectGuid,
        expertApplyId: item.id,
        name: "",
        qualificationTypeId: expertise.qualificationTypeId,
        qualificationType: "",
        qualificationLevelId: 0,
        qualificationLevels: expertise.qualificationLevels.map(s => ({ id : crypto.randomUUID(), subjectId: subjectGuid, levelId: s })),
        expertiseType: expertise.expertiseType !== undefined ? expertise.expertiseType.reduce(function (a, b) { return a + b; }, 0) : ExpertiseType.None,
        selectedAssessmentActivities: expertise.selectedAssessmentActivities !== undefined ? expertise.selectedAssessmentActivities.reduce(function (a, b) { return a + b; }, 0) : AssessmentActivities.None,
        teachingActivity: expertise.teachingActivity !== undefined ? expertise.teachingActivity.reduce(function (a, b) { return a + b; }, 0): TeachingActivity.None,
        hadQual: false
      }  
    dispatch(addExpertise(item.id, subject));
    onClose();
    };
    
    let assessmentRequired = expertiseType !== undefined && expertiseType.some(x => x === ExpertiseType.Assessment);
    let teachingRequired = expertiseType !== undefined && expertiseType.some(x => x === ExpertiseType.Teaching);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="add-expertise-dialog"
    >
      <DialogTitle data-test="activity-title">Add expertise</DialogTitle>
      <Divider component="div" />
      <DialogContent style={{ minHeight: "250px" }}>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
                <Controller
                  control={control}
                  name={"subjectId"}
                  rules={{
                    required: true,
                  }}
                  as={
                    <Select
                      label="Subjects"
                      datatest="subject-add-expertise-dialog"
                      required
                    >
                      {subjectLookup}
                    </Select>
                  }
                />
            </Grid>
          </Grid>
          {errors.subjectId && (
            <Error data-test="subject-add-expertise-dialog-error">
              This field is required
            </Error>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={"qualificationTypeId"}
                rules={{ required: true,}}
                as={
                  <Select
                    label="Qualification Type"
                    datatest="qualification-type-add-expertise-dialog"
                    required
                  >
                    {qualificationTypesLookupsLocal}
                  </Select>
                }
              />
            </Grid>
          </Grid>
          {errors.qualificationTypeId && (
            <Error data-test="qualification-type-add-expertise-dialog-error">
              This field is required
            </Error>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                rules={{
                  validate: (value) => {
                    if (value === undefined || value.length === 0) return false;
                    else return true;
                  }
                }}
                name={"qualificationLevels"}
                as={
                  <Select
                    label="Qualification levels "
                    datatest="qualification-level-add-expertise-dialog"
                    selectVariant="multiple"
                    required
                  >
                    {qualificationLevelsLookups.filter(x => x.id !== -1)}
                  </Select>
                }
              />
            </Grid>
          </Grid>
          {errors.qualificationLevels && (
            <Error data-test="qualification-level-add-expertise-dialog-error">
              This field is required
            </Error>
          )}


          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={"expertiseType"}
                rules={{
                  validate: (value) => {
                    if (value === undefined || value.length === 0) return false;
                    else return true;
                  }
                }}
                as={
                  <Select
                    label="Expertise "
                    datatest="expertise-add-expertise-dialog"
                    selectVariant="multiple"
                    required
                  >
                    {assessmentActivityLookup}
                  </Select>
                }
              />
            </Grid>
          </Grid>
          {errors.expertiseType && (
            <Error data-test="expertise-add-expertise-dialog-error">
              This field is required
            </Error>
          )}

          {assessmentRequired && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name={"selectedAssessmentActivities"}
                  rules={{
                    validate: (value) => {
                      if (value === undefined || value.length === 0) return false;
                      else return true;
                    }
                  }}
                as={
                  <Select
                    label="Assessment expertise "
                    datatest="assessment-add-expertise-dialog"
                    selectVariant="multiple"
                    required
                  >
                    {assessmentExpertiseLookup}
                  </Select>
                }
              />
            </Grid>
            </Grid>            
          )}
          {assessmentRequired && errors.selectedAssessmentActivities && (
            <Error data-test="expertise-add-selectedAssessmentActivities-dialog-error">
              This field is required
            </Error>
          )}

          {teachingRequired && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name={"teachingActivity"}
                  rules={{
                    validate: (value) => {
                      if (value === undefined || value.length === 0) return false;
                      else return true;
                    }
                  }}
                  as={
                    <Select
                      label="Teaching expertise "
                      datatest="teaching-add-expertise-dialog"
                      selectVariant="multiple"
                      required
                    >
                      {teachingExpertiseLookupsLocal}
                    </Select>
                  }
                />
              </Grid>
            </Grid>)}
          {teachingRequired && errors.teachingActivity && (
            <Error data-test="expertise-add-selectedAssessmentActivities-dialog-error">
              This field is required
            </Error>
          )}
          
          
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="cancel-add-expertise-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          data-test="commit-add-expertise-button"
        >
          Commit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

